import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Admin 전역/부분 에러 경계.
 *
 * - 루트에서는 `AppErrorFallback` 전체 화면 폴백 사용
 * - 탭/섹션 단위에서는 `scope="section"` 으로 카드형 폴백 사용 → 한 탭이 깨져도
 *   다른 탭/대시보드는 계속 동작
 * - `resetKeys` 가 바뀌면 경계 내부 상태가 리셋되어 재렌더 시도
 *   (예: 탭 전환 시 activeTab 을 전달)
 *
 * React error boundary 는 비동기/이벤트 핸들러 안에서 throw 된 에러는 잡지
 * 못한다. 렌더/라이프사이클에서 발생한 동기 에러만 대상. 이 한계 때문에
 * 데이터 패칭 훅(`useFaqData`) 측에서 입력 데이터를 `normalize()` 로 방어
 * 하는 것이 본질적인 수정이고, 이 boundary 는 최후의 방어선이다.
 */

interface ErrorBoundaryProps {
  children: ReactNode;
  /** 'app' = 전체 화면 폴백, 'section' = 카드형 폴백 */
  scope?: 'app' | 'section';
  /** 섹션 폴백 상단에 표시할 라벨 */
  label?: string;
  /** 이 배열이 바뀌면 경계 내부가 리셋됨 */
  resetKeys?: ReadonlyArray<unknown>;
  /** 로깅/리포팅 훅 (Sentry 등) */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.label ?? this.props.scope ?? 'root', error, info);
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error === null) return;
    const prev = prevProps.resetKeys ?? [];
    const curr = this.props.resetKeys ?? [];
    if (prev.length !== curr.length || prev.some((k, i) => k !== curr[i])) {
      this.setState({ error: null });
    }
  }

  private reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const scope = this.props.scope ?? 'app';
    const showStack = import.meta.env.DEV;

    if (scope === 'section') {
      return (
        <div className="m-4 rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-5">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/50">
              <i className="ri-alert-line text-red-500 dark:text-red-400 text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-red-700 dark:text-red-300">
                {this.props.label ?? '섹션'} 렌더링 중 오류가 발생했습니다
              </h3>
              <p className="mt-1 text-xs text-red-600/80 dark:text-red-400/80 break-words">
                {error.message || String(error)}
              </p>
              {showStack && error.stack && (
                <pre className="mt-3 max-h-48 overflow-auto text-[11px] leading-relaxed text-red-700 dark:text-red-300 bg-white/60 dark:bg-black/30 border border-red-200 dark:border-red-900 rounded-md p-2 whitespace-pre-wrap">
                  {error.stack}
                </pre>
              )}
              <div className="mt-3 flex gap-2">
                <button
                  onClick={this.reset}
                  className="px-3 py-1.5 rounded-md bg-red-500 hover:bg-red-600 text-white text-xs font-semibold cursor-pointer"
                >
                  다시 시도
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="px-3 py-1.5 rounded-md border border-red-300 dark:border-red-800 text-red-600 dark:text-red-300 text-xs font-semibold hover:bg-red-100 dark:hover:bg-red-900/40 cursor-pointer"
                >
                  새로고침
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4">
        <div className="w-full max-w-lg rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-50 dark:bg-red-950">
              <i className="ri-error-warning-line text-red-500 dark:text-red-400 text-xl" />
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900 dark:text-gray-100">예기치 못한 오류가 발생했습니다</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">관리자 화면에서 문제가 발생했습니다.</p>
            </div>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 break-words">
            {error.message || String(error)}
          </p>
          {showStack && error.stack && (
            <pre className="mt-4 max-h-64 overflow-auto text-[11px] leading-relaxed text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md p-3 whitespace-pre-wrap">
              {error.stack}
            </pre>
          )}
          <div className="mt-5 flex gap-2">
            <button
              onClick={this.reset}
              className="flex-1 py-2.5 rounded-md bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold hover:bg-black dark:hover:bg-gray-100 cursor-pointer"
            >
              다시 시도
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex-1 py-2.5 rounded-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
            >
              새로고침
            </button>
          </div>
        </div>
      </div>
    );
  }
}
